import React, { useState, useRef } from "react";
import {
  Grid,
  TextField,
  Button,
  ButtonGroup,
  Autocomplete,
  FormControl,
  Box,
  Typography,
  useTheme,
  CircularProgress,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import HistoryIcon from "@mui/icons-material/History";
import ErrorIcon from "@mui/icons-material/Error";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import UploadHistoricalSignalsModal from "./UploadHistoricalSignalsModal";
import { addHistoricalSignals } from "../../../utils/restAPI";
import { useDispatch } from "react-redux";
import {
  updateLoading,
  updateLoadingMsg,
} from "../../../Redux/Reducers/uiSlice";
import { extractTickerAndWeight } from "../../../component/Methods";
import { toast } from "react-toastify";

const SignalsControls = ({
  strategyList,
  strategyVal,
  setStrategyVal,
  strategyValObj,
  strategySelected,
  strategies,
  pasted,
  setPasted,
  setVerifyEnabled,
  setCopiedData,
  setHandleVerify,
  setSaveChangeHandler,
  setNumberOfInvalidObjects,
  setEmpty,
  setError,
  error,
  empty,
  numberOfInvalidObjects,
  setSaveChange,
  onFetchHistoricalSignals,
  isFetchingHistoricalSignals,
  onFetchAllSignals,
  isFetchingAllSignals,
  isManager,
  managerId,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  
  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // Split button state
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const options = ['Fetch Rebalanced Signals', 'Fetch All Signals'];

  const reset = () => {
    setVerifyEnabled(false);
    setCopiedData([]);
    setNumberOfInvalidObjects(0);
    setEmpty(false);
    setError("");
  };

  const pasteFromClipboard = async () => {
    try {
      const entries = await navigator.clipboard.readText();

      // Try JSON first (fastest path)
      try {
        const result = JSON.parse(entries);
        if (
          Array.isArray(result) &&
          result.every((obj) => typeof obj === "object")
        ) {
          setPasted(result);
          reset();
          return;
        }
      } catch (jsonParseError) {
        // Continue to text processing
      }

      // Process as text - use Web Worker for anything over 50 entries
      const lines = entries.split("\n").filter((entry) => entry.trim() !== "");

      if (lines.length > 50) {
        // Use Web Worker for anything over 50 entries
        await processWithWebWorker(lines);
      } else {
        // For small datasets, process immediately without loading states
        const processedResult = processLinesImmediately(lines);
        setPasted(processedResult);
        reset();
      }
    } catch (err) {
      console.error("Paste error:", err);
      setPasted([]);
      alert("Error: Cannot paste from clipboard");
    }
  };

  const processLinesImmediately = (lines) => {
    const results = [];
    for (let i = 0; i < lines.length; i++) {
      const extracted = extractTickerAndWeight(lines[i]);
      if (extracted) {
        results.push(extracted);
      }
    }
    return results;
  };

  const processWithWebWorker = (lines) => {
    return new Promise((resolve, reject) => {
      // Show loading state only for larger datasets
      if (lines.length > 100) {
        dispatch(updateLoading(true));
        dispatch(updateLoadingMsg(`Processing ${lines.length} entries...`));
      }

      // Try to create Web Worker with fallback
      let worker;
      try {
        worker = new Worker("/tickerProcessor.js");
      } catch (workerError) {
        console.warn(
          "Web Worker failed to load, falling back to main thread processing:",
          workerError
        );
        // Fallback to main thread processing
        const results = processLinesImmediately(lines);
        setPasted(results);
        reset();
        dispatch(updateLoading(false));
        resolve(results);
        return;
      }

      worker.onmessage = function (e) {
        const { type, progress, processed, total, results, error } = e.data;

        switch (type) {
          case "progress":
            if (lines.length > 100) {
              dispatch(
                updateLoadingMsg(`Processing ${total} entries... ${progress}%`)
              );
            }
            break;

          case "complete":
            setPasted(results);
            reset();
            if (lines.length > 100) {
              toast.success(`Processed ${results.length} entries`);
            }
            dispatch(updateLoading(false));
            worker.terminate();
            resolve(results);
            break;

          case "error":
            console.error("Web Worker error:", error);
            toast.error("Error processing clipboard data");
            dispatch(updateLoading(false));
            worker.terminate();
            reject(new Error(error));
            break;
        }
      };

      worker.onerror = function (error) {
        console.error("Web Worker error:", error);
      

        const results = processLinesImmediately(lines);
        setPasted(results);
        reset();
        dispatch(updateLoading(false));
        worker.terminate();
        resolve(results);
      };

      // Start processing with larger batch size for better performance
      worker.postMessage({
        lines: lines,
        batchSize: Math.max(1000, Math.floor(lines.length / 10)),
      });
    });
  };

  // Split button handlers
  const handleClick = () => {
    if (selectedIndex === 0) {
      onFetchHistoricalSignals();
    } else {
      onFetchAllSignals();
    }
  };

  const handleMenuItemClick = (event, index) => {
    setSelectedIndex(index);
    setOpen(false);
    if (index === 0) {
      onFetchHistoricalSignals();
    } else {
      onFetchAllSignals();
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target)
    ) {
      return;
    }
    setOpen(false);
  };

  const isFetching = isFetchingHistoricalSignals || isFetchingAllSignals;

  const handleUploadHistoricalSignals = async (parsedData) => {
    if (!strategyValObj || !managerId) {
      toast.error("No strategy or manager selected.");
      return;
    }
    try {
      const response = await addHistoricalSignals({
        strategyId: strategyValObj,
        managerId,
        data: parsedData,
      });
      if (response?.error) {
        toast.error(response.message || "Failed to upload historical signals.");
      } else {
        toast.success("Historical signals uploaded successfully.");
      }
    } catch (err) {
      toast.error("Failed to upload historical signals.");
    }
  };

  return (
    <Box>
      <UploadHistoricalSignalsModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUploadHistoricalSignals}
      />
      <Grid container spacing={3} alignItems="center">
        {/* Strategy Selection */}
        <Grid size={{ xs: 12, md: 6 }} >
          <FormControl fullWidth>
            <Autocomplete
              options={strategyList || []}
              value={strategyVal || null}
              autoSelect
              isOptionEqualToValue={(option, value) => option === value}
              onChange={(e, newValue) => {
                setStrategyVal(newValue || "");

                // Clear pasted data when strategy changes
                setPasted([]);

                const strObj = strategies?.find((i) => {
                  return i?.name === newValue;
                });
                if (strObj && strObj?.strategyId) {
                  strategySelected(strObj?.strategyId);
                } else {
                  // Clear strategy object when no strategy is selected
                  strategySelected(null);
                  // Clear all related data when no strategy is selected
                  setCopiedData([]);
                  setNumberOfInvalidObjects(0);
                  setEmpty(false);
                  setError("");
                }
              }}
              getOptionLabel={(option) => option || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Strategy to Manage"
                  variant="outlined"
                  fullWidth
                  size="small"
                />
              )}
            />
          </FormControl>
        </Grid>

        {/* Action Buttons */}
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex", justifyContent: { xs: "stretch", md: "flex-end" } }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              alignItems: "center",
              flexWrap: { xs: "wrap", md: "nowrap" },
              width: { xs: "100%", md: "auto" },
            }}
          >
                                                   {/* Error Messages */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mr: 2,
                  gap: 1,
                  flexWrap: "wrap",
                  maxWidth: 400,
                }}
              >
                {error && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 0.5,
                      backgroundColor: theme.palette.error.dark,
                      color: theme.palette.error.contrastText,
                      fontSize: "11px",
                      fontWeight: 500,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      border: `1px solid ${theme.palette.error.main}`,
                      maxWidth: 400,
                      wordWrap: "break-word",
                    }}
                  >
                    <ErrorIcon sx={{ fontSize: 14, color: theme.palette.error.contrastText }} />
                    {error}
                  </Box>
                )}
              </Box>

            {isManager && (
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled={!strategyVal}
                onClick={() => setUploadModalOpen(true)}
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  color: `${theme.palette.warning.main} !important`,
                  borderColor: `${theme.palette.warning.main} !important`,
                  "&.MuiButton-outlined": {
                    color: `${theme.palette.warning.main} !important`,
                    borderColor: `${theme.palette.warning.main} !important`,
                  },
                  "&:hover": {
                    color: `${theme.palette.warning.dark} !important`,
                    borderColor: `${theme.palette.warning.dark} !important`,
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 152, 0, 0.14) !important"
                        : "rgba(255, 152, 0, 0.08) !important",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 152, 0, 0.14) !important"
                        : "rgba(255, 152, 0, 0.08) !important",
                    boxShadow: "none !important",
                    transform: "none !important",
                  },
                }}
              >
                Upload Historical Signals
              </Button>
            )}

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={
                !strategyVal || Boolean(error) || numberOfInvalidObjects > 0
              }
              onClick={() => {
                setSaveChange(true);
              }}
              sx={{
                borderRadius: 1,
                textTransform: "none",
                fontWeight: 500,
                whiteSpace: "nowrap",
                flexShrink: 0,
                background: theme.palette.success.main,
                backgroundColor: theme.palette.success.main,
                color: "#ffffff",
                "&:hover": {
                  background: theme.palette.success.dark,
                  backgroundColor: theme.palette.success.dark,
                },
                "&.Mui-disabled": {
                  background: theme.palette.action.disabledBackground,
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              Update Signals
            </Button>
            <ButtonGroup
              variant="contained"
              ref={anchorRef}
              aria-label="split button"
              disabled={!strategyVal || isFetching}
              sx={{
                "& .MuiButton-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&:disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                  },
                },
              }}
            >
              <Button
                onClick={handleClick}
                startIcon={isFetching ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <HistoryIcon />}
              >
                {options[selectedIndex]}
              </Button>
              <Button
                size="small"
                aria-controls={open ? 'split-button-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-label="select fetch option"
                aria-haspopup="menu"
                onClick={handleToggle}
                sx={{
                  minWidth: '40px',
                }}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
            <Popper
              sx={{ zIndex: 1 }}
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              transition
              disablePortal
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === 'bottom' ? 'center top' : 'center bottom',
                  }}
                >
                  <Paper>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList id="split-button-menu" autoFocusItem>
                        {options.map((option, index) => (
                          <MenuItem
                            key={option}
                            selected={index === selectedIndex}
                            onClick={(event) => handleMenuItemClick(event, index)}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SignalsControls;
